import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { MailCheck, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';

interface PendingSamenInvitesProps {
  samenId: string;
  /** Bumped by the page after an invite is sent, to re-fetch the list. */
  refreshKey?: number;
}

interface PendingInvite {
  id: string;
  naam: string;
  email: string;
  kind: string;
  expires_at: string;
}

/**
 * Lists invites that have been sent but not yet accepted, so the group can see
 * who is still outstanding and withdraw one that went to the wrong address.
 *
 * Covers both kinds: someone who already had an account, and someone who was
 * sent a signup link instead. The latter is marked, because until they
 * register there is no account behind the address.
 */
export function PendingSamenInvites({
  samenId,
  refreshKey = 0,
}: PendingSamenInvitesProps) {
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchInvites = useCallback(async () => {
    const { data, error } = await supabase
      .from('samen_invite')
      .select('id, naam, email, kind, expires_at')
      .eq('samen_id', samenId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching samen invites:', error);
    } else {
      setInvites((data as PendingInvite[]) ?? []);
    }
    setLoading(false);
  }, [samenId]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites, refreshKey]);

  const revoke = async (id: string, naam: string) => {
    setRevoking(id);
    try {
      // Not a plain update: for a signup invite the real credential is the
      // user_invite row, which only the function can reach.
      const { error } = await supabase.rpc('revoke_samen_invite', {
        p_id: id,
      });
      if (error) throw error;

      setInvites((prev) => prev.filter((i) => i.id !== id));
      toast({
        title: 'Uitnodiging ingetrokken',
        description: `De uitnodiging voor ${naam} werkt niet meer.`,
      });
    } catch (err) {
      console.error('Failed to revoke samen invite:', err);
      toast({
        title: 'Intrekken mislukt',
        description: 'Probeer het later opnieuw.',
        variant: 'destructive',
      });
    } finally {
      setRevoking(null);
    }
  };

  if (loading || invites.length === 0) return null;

  return (
    <div>
      <h3 className="text-sm font-medium mb-2 flex items-center gap-2 text-muted-foreground">
        <MailCheck className="h-4 w-4" />
        Uitgenodigd, nog niet toegevoegd
      </h3>
      <ul className="space-y-1">
        {invites.map((invite) => (
          <li
            key={invite.id}
            className="flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm"
          >
            <span className="min-w-0">
              <span className="font-medium">{invite.naam}</span>{' '}
              <span className="text-muted-foreground break-all">
                {invite.email}
              </span>
              {invite.kind === 'signup' && (
                <span className="block text-xs text-muted-foreground">
                  nog geen account &mdash; uitgenodigd om er een te maken
                </span>
              )}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0 text-muted-foreground hover:text-foreground"
              onClick={() => revoke(invite.id, invite.naam)}
              disabled={revoking === invite.id}
              title="Uitnodiging intrekken"
            >
              <X className="h-4 w-4" />
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
